import { I as Icon, i as iconDefinition } from './definition28.js';
import { E as Elevation, e as elevationDefinition } from './definition65.js';
import { V as VividElement, a as attr, o as observable, h as html, d as createRegisterFunction, f as defineVividComponent } from './vivid-element.js';
import { L as Linkable } from './linkable.js';
import { A as Appearance } from './enums.js';
import { d as delegateAria } from './delegates-aria.js';
import { w as when } from './when.js';
import { s as slotted } from './slotted.js';
import { c as classNames } from './class-names.js';

const styles = ":host{display:flex}.base{border-radius:8px;inline-size:100%;text-align:start}.base{padding:0;border:0 none;outline:none;text-decoration:none}.base:is(button,a):not(:disabled){cursor:pointer}.base:is(button,a):disabled{cursor:not-allowed}.base:is(button,a):focus-visible{box-shadow:0 0 0 4px color-mix(in srgb,var(--vvd-color-cta-500),transparent 85%),inset 0 0 0 3px var(--focus-stroke-gap-color, currentColor);outline:1px solid var(--focus-stroke-color, var(--vvd-color-cta-500));outline-offset:calc(-1px - var(--focus-inset, 0px))}.wrapper{display:flex;overflow:hidden;flex-flow:column;border-radius:inherit;block-size:100%;color:var(--vvd-color-canvas-text)}.wrapper{--_appearance-color-text: var(--_connotation-color-firm);--_appearance-color-fill: transparent;--_appearance-color-outline: transparent}.wrapper:where(.hover,:hover):where(:not(.disabled,:disabled,.readonly)){--_appearance-color-text: var(--_connotation-color-firm);--_appearance-color-fill: var(--_connotation-color-faint);--_appearance-color-outline: transparent}.wrapper:where(.active,:active):where(:not(.disabled,:disabled)){--_appearance-color-text: var(--_connotation-color-firm);--_appearance-color-fill: var(--_connotation-color-soft);--_appearance-color-outline: transparent}.wrapper{--_connotation-color-primary: var(--vvd-card-cta-primary, var(--vvd-color-cta-500));--_connotation-color-primary-text: var(--vvd-card-cta-primary-text, var(--vvd-color-canvas));--_connotation-color-primary-increment: var(--vvd-card-cta-primary-increment, var(--vvd-color-cta-600));--_connotation-color-faint: var(--vvd-card-cta-faint, var(--vvd-color-cta-50));--_connotation-color-soft: var(--vvd-card-cta-soft, var(--vvd-color-cta-100))}.base:is(button,a):hover>.wrapper{background-color:var(--_appearance-color-fill)}.main-content{display:flex;flex-flow:column;padding:24px}.header{display:flex;flex:1;align-items:flex-start;gap:8px}.header-content{flex:1}.header-wrapper{display:flex;gap:8px}.header-headline,.header-subtitle{display:-webkit-box;overflow:hidden;-webkit-box-orient:vertical}.header-headline{font:var(--vvd-typography-heading-4);-webkit-line-clamp:var(--headline-line-clamp)}.header-subtitle{font:var(--vvd-typography-base);-webkit-line-clamp:var(--subtitle-line-clamp)}.hide-header .header{display:none}.header-subtitle,.text{color:var(--vvd-color-neutral-600);font:var(--vvd-typography-base)}.text{padding-right:8px;margin-top:12px}.footer{display:inline-flex;flex-direction:column;align-items:flex-end;padding-bottom:24px;margin-top:auto;padding-inline:24px}.hide-footer .footer{display:none}::slotted([slot=graphic i]),.icon{flex-shrink:0;align-self:baseline;margin-block-start:4px}.icon{font-size:20px}::slotted([slot=meta i]){flex-shrink:0;align-self:flex-start;margin-block-start:-8px;margin-inline:auto -8px}";

var __defProp = Object.defineProperty;
var __decorateClass = (decorators, target, key, kind) => {
  var result = void 0 ;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (decorator(target, key, result) ) || result;
  if (result) __defProp(target, key, result);
  return result;
};
class Card extends Linkable(VividElement) {
  constructor() {
    super(...arguments);
    this.clickableCard = false;
  }
}
__decorateClass([
  attr
], Card.prototype, "appearance");
__decorateClass([
  attr
], Card.prototype, "headline");
__decorateClass([
  attr
], Card.prototype, "subtitle");
__decorateClass([
  attr
], Card.prototype, "text");
__decorateClass([
  attr
], Card.prototype, "icon");
__decorateClass([
  attr
], Card.prototype, "elevation");
__decorateClass([
  attr({
    mode: "boolean",
    attribute: "clickable-card"
  })
], Card.prototype, "clickableCard");
__decorateClass([
  observable
], Card.prototype, "footerSlottedContent");
__decorateClass([
  observable
], Card.prototype, "graphicSlottedContent");
__decorateClass([
  observable
], Card.prototype, "hasMetaSlottedContent");

const getClasses = (_) => classNames(
  "base",
  ["hide-footer", !_.footerSlottedContent || !_.footerSlottedContent.length],
  ["hide-header", shouldHideHeader(_)]
);
function renderHeaderIcon(iconTag) {
  return html`
		<${iconTag} class="icon" inline name="${(x) => x.icon}"></${iconTag}>`;
}
function Headline() {
  return html` <div class="header-headline">${(x) => x.headline}</div> `;
}
function Subtitle() {
  return html` <div class="header-subtitle">${(x) => x.subtitle}</div> `;
}
function headerContent() {
  return html`
		<div class="header-content">
			${when((x) => x.headline, Headline())}
			${when((x) => x.subtitle, Subtitle())}
		</div>
	`;
}
function renderHeader(iconTag) {
  return html` <div class="header">
		<slot name="graphic" ${slotted("graphicSlottedContent")}
			>${when((x) => x.icon, renderHeaderIcon(iconTag))}</slot
		>
		${when((x) => x.headline || x.subtitle, headerContent())}
	</div>`;
}
function shouldHideHeader(card) {
  return !card.headline && !card.subtitle && !card.icon && (!card.graphicSlottedContent || !card.graphicSlottedContent.length);
}
function renderMetaSlot() {
  return html` <slot name="meta" ${slotted("metaSlottedContent")}></slot> `;
}
function text() {
  return html` <div class="text">${(x) => x.text}</div> `;
}
function renderButtonElement(content) {
  return html`<button
		class="${getClasses}"
		type="button"
		${delegateAria()}
	>
		${content}
	</button>`;
}
function renderCardBaseElement(x, content) {
  if (x.href) {
    return x._renderLinkElement(content, getClasses);
  } else if (x.clickableCard) {
    return renderButtonElement(content);
  } else {
    return html`<div class="${getClasses}">${content}</div>`;
  }
}
function renderCardContent(context) {
  const iconTag = context.tagFor(Icon);
  return html`
		${(x) => renderCardBaseElement(
    x,
    html`<div class="wrapper">
					<div class="vwc-card-media">
						<slot name="media"></slot>
					</div>
					<slot name="main">
						<div class="main-content">
							<div class="header-wrapper">
								${renderHeader(iconTag)} ${renderMetaSlot()}
							</div>
							${when((x2) => x2.text, text())}
						</div>
					</slot>
					<div class="footer">
						<slot name="footer" ${slotted("footerSlottedContent")}></slot>
					</div>
				</div> `
  )}
	`;
}
const CardTemplate = (context) => {
  const elevationTag = context.tagFor(Elevation);
  return html`
		${when(
    (x) => x.appearance !== Appearance.Ghost && x.appearance !== Appearance.Outlined,
    html`
			<${elevationTag} dp=${(x) => x.elevation ?? "4"}>
			  ${renderCardContent(context)}
			</${elevationTag}>
		`
  )}
		${when(
    (x) => x.appearance === Appearance.Outlined,
    html`
			<${elevationTag} dp='0')}>
			  ${renderCardContent(context)}
			</${elevationTag}>
		`
  )}
		${when(
    (x) => x.appearance === Appearance.Ghost,
    html` ${renderCardContent(context)} `
  )}
	`;
};

const cardDefinition = defineVividComponent(
  "card",
  Card,
  CardTemplate,
  [iconDefinition, elevationDefinition],
  {
    styles
  }
);
const registerCard = createRegisterFunction(cardDefinition);

export { Card as C, cardDefinition as c, registerCard as r };
